import { createContext, useContext, useState, useEffect, useCallback } from "react";
import {
  getConsent,
  saveConsent,
  grantAnalytics,
  disableAnalytics,
  pageview,
} from "../lib/analytics";

const ConsentContext = createContext(null);

export function ConsentProvider({ children }) {
  const [consent, setConsentState] = useState(() => getConsent()); // { status, version, timestamp } | null
  const [settingsOpen, setSettingsOpen] = useState(false);

  // On load, honor a previously granted choice (analytics only, ads never).
  useEffect(() => {
    if (getConsent()?.status === "accepted") {
      grantAnalytics();
    }
  }, []);

  const accept = useCallback(() => {
    const record = saveConsent("accepted");
    setConsentState(record);
    grantAnalytics();
    setSettingsOpen(false);
    pageview(window.location.pathname + window.location.search);
  }, []);

  const reject = useCallback(() => {
    const record = saveConsent("rejected");
    setConsentState(record);
    disableAnalytics();
    setSettingsOpen(false);
    // If analytics were already running this session, reload so the in-memory
    // GA/PostHog instances are torn down and nothing re-initializes (status is
    // now "rejected"). Cookies were already cleared by disableAnalytics().
    if (typeof window !== "undefined" && (window.__ewGaLoaded || window.__ewPhLoaded)) {
      window.location.reload();
    }
  }, []);

  const openSettings = useCallback(() => setSettingsOpen(true), []);
  const closeSettings = useCallback(() => setSettingsOpen(false), []);

  const value = {
    consent,
    status: consent?.status ?? null,
    showBanner: consent === null,
    settingsOpen,
    accept,
    reject,
    openSettings,
    closeSettings,
  };

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    // Safe fallback so a component outside the provider never crashes.
    return {
      consent: null,
      status: null,
      showBanner: false,
      settingsOpen: false,
      accept: () => {},
      reject: () => {},
      openSettings: () => {},
      closeSettings: () => {},
    };
  }
  return ctx;
}
